export type RoleValue = 'view_all_only' | 'clip_only' | 'manager' | 'admin';

export interface Role {
  value: RoleValue;
  label: string;
  description: string;
}

export const ROLES: Role[] = [
  {
    value: 'view_all_only',
    label: 'Visualizador',
    description: 'Pode apenas visualizar clipes e projetos, mas não pode clipar nem enviar novos projetos'
  },
  {
    value: 'clip_only',
    label: 'Clipador',
    description: 'Pode clipar e ver apenas seus próprios vídeos clipados'
  },
  {
    value: 'manager',
    label: 'Gerente',
    description: 'Pode enviar projetos, clipar e ver apenas os seus próprips clipes'
  },
  {
    value: 'admin',
    label: 'Administrador',
    description: 'Mesmo que gerente, mas também pode convidar novos usuários'
  }
];

export const getRoleLabel = (value: string): string => {
  const role = ROLES.find(r => r.value === value);
  return role?.label || value;
};

export const getRoleDescription = (value: string): string => {
  const role = ROLES.find(r => r.value === value);
  return role?.description || '';
};

// Permission checking functions based on role
// Note: Master users have all permissions regardless of their role

export interface RolePermissions {
  // Can view all projects from all users
  canViewAllProjects: boolean;
  // Can view all clips from all users
  canViewAllClips: boolean;
  // Can create new projects (send videos for processing)
  canCreateProjects: boolean;
  // Can clip videos (create clips from projects)
  canClipVideos: boolean;
  // Can invite new users to the team
  canInviteUsers: boolean;
  // Can manage team roles
  canManageRoles: boolean;
}

export const getRolePermissions = (role: string | undefined, isMaster: boolean): RolePermissions => {
  // Master has all permissions
  if (isMaster) {
    return {
      canViewAllProjects: true,
      canViewAllClips: true,
      canCreateProjects: true,
      canClipVideos: true,
      canInviteUsers: true,
      canManageRoles: true,
    };
  }

  switch (role) {
    case 'admin':
      return {
        canViewAllProjects: true,
        canViewAllClips: true,
        canCreateProjects: true,
        canClipVideos: true,
        canInviteUsers: true,
        canManageRoles: true,
      };
    case 'manager':
      return {
        canViewAllProjects: true,
        canViewAllClips: true,
        canCreateProjects: true,
        canClipVideos: true,
        canInviteUsers: false,
        canManageRoles: false,
      };
    case 'clip_only':
      return {
        canViewAllProjects: true, // Can see projects to clip from
        canViewAllClips: false, // Can only see their own clips
        canCreateProjects: false,
        canClipVideos: true,
        canInviteUsers: false,
        canManageRoles: false,
      };
    case 'view_all_only':
      return {
        canViewAllProjects: true,
        canViewAllClips: true,
        canCreateProjects: false,
        canClipVideos: false,
        canInviteUsers: false,
        canManageRoles: false,
      };
    default:
      // Default to most restrictive permissions for unknown roles
      return {
        canViewAllProjects: true,
        canViewAllClips: false,
        canCreateProjects: false,
        canClipVideos: false,
        canInviteUsers: false,
        canManageRoles: false,
      };
  }
};

// Legacy helper functions (kept for backwards compatibility)
export const canManageRoles = (userRole: string | undefined, isMaster: boolean): boolean => {
  return getRolePermissions(userRole, isMaster).canManageRoles;
};

export const canInviteUsers = (userRole: string | undefined, isMaster: boolean): boolean => {
  return getRolePermissions(userRole, isMaster).canInviteUsers;
};
